import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getCustomerChart, getCustomerChartCustom } from '../../../api/report.api';
import styles from '../../../assets/scss/pages/ReportCustomer.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

const PERIODS = [
    { key: 'today', label: 'Hôm nay' },
    { key: 'yesterday', label: 'Hôm qua' },
    { key: '7days', label: '7 ngày' },
    { key: 'thismonth', label: 'Tháng này' },
    { key: 'lastmonth', label: 'Tháng trước' },
    { key: 'custom', label: 'Tuỳ chọn' },
];

const VIEW_MODES = [
    { key: 'hour', label: 'Theo giờ' },
    { key: 'day', label: 'Theo ngày' },
    { key: 'weekday', label: 'Theo thứ' },
];

function formatDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

export default function ReportCustomer() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.REPORT.CUSTOMER_VIEW);

    const [searchParams] = useSearchParams();
    const urlPeriod = searchParams.get('period');
    const urlStart = searchParams.get('startDate');
    const urlEnd = searchParams.get('endDate');

    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(urlPeriod || '7days');
    const [viewMode, setViewMode] = useState('day');
    const [chartData, setChartData] = useState([]);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [startDate, setStartDate] = useState(urlStart || formatDate(new Date()));
    const [endDate, setEndDate] = useState(urlEnd || formatDate(new Date()));

    const fetchIdRef = useRef(0);

    const fetchData = useCallback(async () => {
        if (!canView) { setLoading(false); return; }
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        try {
            let res;
            if (period === 'custom') {
                res = await getCustomerChartCustom({ startDate, endDate });
            } else {
                res = await getCustomerChart({ viewMode, period });
            }
            if (fetchId !== fetchIdRef.current) return;
            setChartData(res.data?.chartData || []);
            setTotalCustomers(res.data?.totalCustomers || 0);
        } catch (err) {
            if (fetchId !== fetchIdRef.current) return;
            setChartData([]);
            setTotalCustomers(0);
        } finally {
            if (fetchId === fetchIdRef.current) setLoading(false);
        }
    }, [canView, period, viewMode, startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePeriodChange = (p) => {
        setPeriod(p);
        if (p !== 'custom') setViewMode('day');
    };

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        setStartDate(val);
        if (val > endDate) setEndDate(val);
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        setEndDate(val);
        if (val < startDate) setStartDate(val);
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </button>
                <h1 className={styles.title}>Báo cáo khách hàng</h1>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
                <div className={styles.periodTabs}>
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            className={`${styles.periodTab}${period === p.key ? ` ${styles.periodTabActive}` : ''}`}
                            onClick={() => handlePeriodChange(p.key)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* Custom date range */}
                {period === 'custom' && (
                    <div className={styles.customDateRow}>
                        <div className={styles.dateGroup}>
                            <span className={styles.dateLabel}>Từ ngày:</span>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={startDate}
                                onChange={handleStartDateChange}
                            />
                        </div>
                        <div className={styles.dateGroup}>
                            <span className={styles.dateLabel}>Đến ngày:</span>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={endDate}
                                onChange={handleEndDateChange}
                            />
                        </div>
                    </div>
                )}

                {/* View mode (only for non-custom periods) */}
                {period !== 'custom' && (
                    <div className={styles.viewModeTabs}>
                        {VIEW_MODES.map((v) => (
                            <button
                                key={v.key}
                                className={`${styles.viewModeTab}${viewMode === v.key ? ` ${styles.viewModeTabActive}` : ''}`}
                                onClick={() => setViewMode(v.key)}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Summary */}
            <div className={styles.summary}>
                <span className={styles.summaryLabel}>Tổng lượt khách:</span>
                <span className={styles.summaryValue}>{totalCustomers.toLocaleString('vi-VN')}</span>
            </div>

            {/* Chart */}
            <div className={styles.chartCard}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} />
                        Đang tải dữ liệu...
                    </div>
                ) : chartData.length === 0 ? (
                    <div className={styles.loading}>Không có dữ liệu trong khoảng thời gian này</div>
                ) : (
                    <div className={styles.chartWrap}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#ECEEF2" />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 12, fill: '#6B7280' }}
                                    tickLine={false}
                                    axisLine={{ stroke: '#ECEEF2' }}
                                />
                                <YAxis
                                    tick={{ fontSize: 12, fill: '#6B7280' }}
                                    tickLine={false}
                                    axisLine={false}
                                    allowDecimals={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: 10,
                                        border: '1px solid #ECEEF2',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        fontSize: 13,
                                    }}
                                    formatter={(value) => [`${value} lượt`, 'Khách']}
                                    labelFormatter={(label) => label}
                                />
                                <Bar
                                    dataKey="value"
                                    fill="#0098F6"
                                    radius={[6, 6, 0, 0]}
                                    maxBarSize={40}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Note */}
            <div className={styles.note}>
                <div className={styles.noteTitle}>Lưu ý</div>
                <div className={styles.noteText}>
                    Lượt khách ghé là tổng số lần phát sinh đơn trong ngày. Vì vậy, nếu một khách mua 2 lần
                    trong cùng ngày thì tính là 2 lượt ghé, nhưng vẫn chỉ là 1 khách.
                </div>
            </div>
        </div>
    );
}
